import ellipsify from 'utils/ellipsify'

interface IStringifyOptions {
  maxLength: number
}

/**
 * Stringifies any value to show in the Tree view, ellipsifying as necessary.
 * Any objects / arrays after depth 0 are always ellipsified.
 *
 * @param value The value to stringify
 * @param options.maxLength Maximum allowed length of the returned string
 */
const treeStringify = (
  value: any,
  { maxLength }: IStringifyOptions = { maxLength: 100 }
): string => {
  if (value === undefined) {
    return 'undefined'
  }

  if (value === null) {
    return 'null'
  }

  if (typeof value === 'string') {
    return `"${ellipsify(value, { maxLength: maxLength - 2 })}"` // -2 to account for the extra " marks around the string
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return ellipsify(`${value}`, { maxLength })
  }

  // Called for stringifying any values after depth 0
  const _deepStringify = (v: any) => {
    if (typeof v === 'string') {
      return `"${v}"`
    }

    if (typeof v === 'number' || typeof v === 'boolean') {
      return `${v}`
    }

    if (Array.isArray(v)) {
      return `[ … ]`
    }

    return `{ … }`
  }

  // The following code tries to greedily add as many values as it possibly can to the stringified value

  if (Array.isArray(value)) {
    maxLength -= `[  ]`.length
    const items: string[] = []
    value.some(v => {
      const tmp = _deepStringify(v)
      const estimatedLength = tmp.length + ', '.length // The final string that will be added would also include the extra comma & empty space, take that into account too
      // Even if this is the last item and doesn't technically NEED the extra comma, it is less complicated to assume it does. Worst case scenario, it gets ellipsified, not the end of the world.

      if (estimatedLength < maxLength) {
        // If this item will fit into the string, add it and reduce the maxLength accordingly
        items.push(tmp)
        maxLength -= estimatedLength
        return false
      }

      // If not, return early, it means the next items won't be processed
      return true
    })

    if (items.length === 0) {
      // No items fit
      return `[ … ]`
    }

    if (items.length === value.length) {
      // All items fit
      return `[ ${items.join(', ')} ]`
    }

    // Some items fit
    return `[ ${items.join(', ')}, … ]`
  }

  const objectKeys = Object.keys(value)
  maxLength -= `{  }`.length
  const items: string[] = []
  objectKeys.some(key => {
    const tmp = `${key}: ${_deepStringify(value[key])}`
    const estimatedLength = tmp.length + ', '.length // The final string that will be added would also include the extra comma & empty space, take that into account too
    // Even if this is the last item and doesn't technically NEED the extra comma, it is less complicated to assume it does. Worst case scenario, it gets ellipsified, not the end of the world.

    if (estimatedLength < maxLength) {
      // If this item will fit into the string, add it and reduce the maxLength accordingly
      items.push(tmp)
      maxLength -= estimatedLength
      return false
    }

    // If not, return early, it means the next items won't be processed
    return true
  })

  if (items.length === 0) {
    // No items fit
    return `{ … }`
  }

  if (items.length === objectKeys.length) {
    // All items fit
    return `{ ${items.join(', ')} }`
  }

  // Some items fit
  return `{ ${items.join(', ')}, … }`
}

export default treeStringify
